import { type Editor } from '@tiptap/react'
import katex from 'katex'
import { Trash2 } from 'lucide-react'
import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react'
import { createPortal } from 'react-dom'
import { useI18n } from '@/i18n/i18n'
import { isComposing } from '@/lib/ime'
import { type MathStore, type MathTarget } from './mathStore'

// Formulas in a card are drawn with KaTeX; this is where a person writes one:
// a click on a formula, or `/` → formula. The LaTeX is edited as text with a
// live preview, like the AI writes it (`$…$`, `$$…$$`).

export function MathEditor({ editor, store }: { editor: Editor; store: MathStore }) {
  const target = useSyncExternalStore(store.subscribe, store.getSnapshot)
  if (!target || editor.isDestroyed || !editor.isEditable) return null
  return (
    <MathPopover
      key={`${target.kind}:${target.pos ?? 'new'}`}
      editor={editor}
      target={target}
      close={() => {
        store.open(null)
      }}
    />
  )
}

function MathPopover({
  editor,
  target,
  close,
}: {
  editor: Editor
  target: MathTarget
  close: () => void
}) {
  const { t } = useI18n()
  const [latex, setLatex] = useState(target.latex)
  const input = useRef<HTMLTextAreaElement>(null)
  /** Finished (committed, cancelled or deleted): the blur that follows must not commit again. */
  const done = useRef(false)
  const at = useMemo(() => {
    const pos = target.pos ?? editor.state.selection.from
    const c = editor.view.coordsAtPos(Math.min(pos, editor.state.doc.content.size))
    return { x: Math.round(c.left), y: Math.round(c.bottom) }
  }, [editor, target])
  const preview = useMemo(() => {
    try {
      return katex.renderToString(latex || '\\;', {
        displayMode: target.kind === 'block',
        throwOnError: false,
      })
    } catch {
      return ''
    }
  }, [latex, target.kind])

  useEffect(() => {
    input.current?.focus()
    input.current?.select()
  }, [])

  /** Leave the caret just after the formula, so typing goes on instead of replacing it. */
  const after = (pos: number | null) => {
    if (pos === null) return
    const node = editor.state.doc.nodeAt(pos)
    if (node) editor.commands.setTextSelection(pos + node.nodeSize)
  }

  const commit = () => {
    if (done.current) return
    done.current = true
    const value = latex.trim()
    const c = editor.chain().focus()
    if (target.pos === null) {
      if (value)
        (target.kind === 'block'
          ? c.insertBlockMath({ latex: value })
          : c.insertInlineMath({ latex: value })
        ).run()
      else c.run()
    } else if (!value) {
      ;(target.kind === 'block'
        ? c.deleteBlockMath({ pos: target.pos })
        : c.deleteInlineMath({ pos: target.pos })
      ).run()
    } else {
      ;(target.kind === 'block'
        ? c.updateBlockMath({ latex: value, pos: target.pos })
        : c.updateInlineMath({ latex: value, pos: target.pos })
      ).run()
    }
    after(target.pos)
    close()
  }

  return createPortal(
    <div
      data-math-editor=""
      className="fixed z-50 w-80 rounded-lg border border-line bg-surface p-2 shadow-lg"
      style={{ left: at.x, top: at.y + 6 }}
    >
      <textarea
        ref={input}
        value={latex}
        rows={2}
        aria-label={t('math.label')}
        placeholder={t('math.placeholder')}
        onChange={(e) => {
          setLatex(e.target.value)
        }}
        onKeyDown={(e) => {
          if (isComposing(e)) return
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            commit()
          } else if (e.key === 'Escape') {
            e.preventDefault()
            done.current = true
            editor.commands.focus()
            after(target.pos)
            close()
          }
        }}
        onBlur={commit}
        className="w-full resize-none rounded-md bg-surface-2 px-2 py-1.5 font-mono text-[12.5px] outline-none placeholder:text-muted"
      />
      <div
        className="mt-1.5 min-h-8 overflow-x-auto px-1 text-[13.5px]"
        // KaTeX output of the person's own LaTeX, rendered with trust off.
        dangerouslySetInnerHTML={{ __html: preview }}
      />
      <div className="mt-1 flex items-center justify-between text-[11.5px] text-muted">
        <span>{t('math.hint')}</span>
        {target.pos !== null && (
          <button
            type="button"
            title={t('math.remove')}
            aria-label={t('math.remove')}
            onMouseDown={(e) => {
              e.preventDefault()
            }}
            onClick={() => {
              done.current = true
              const pos = target.pos ?? 0
              const c = editor.chain().focus()
              ;(target.kind === 'block'
                ? c.deleteBlockMath({ pos })
                : c.deleteInlineMath({ pos })
              ).run()
              close()
            }}
            className="inline-flex h-6 w-6 items-center justify-center rounded-md hover:bg-surface-2 hover:text-ink"
          >
            <Trash2 size={13} />
          </button>
        )}
      </div>
    </div>,
    document.body,
  )
}
